import { HomepageCompositionRoot, VisitListener } from '@backstage/plugin-home';
import { Route } from 'react-router-dom';
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { TechDocsIndexPage, techdocsPlugin } from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { CustomTechDocsPage } from './components/techdocs/CustomTechDocsPage';
import { CustomTechDocsReaderPage } from './components/techdocs/CustomTechDocsReaderLayout';
import { CustomCatalogPage } from './components/catalog/CustomCatalogPage';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis, puzzleOIDCAuthApiRef } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { UnifiedThemeProvider } from '@backstage/theme';
import { puzzleTheme } from './theme/puzzleTheme';
import { puzzleDarkTheme } from './theme/puzzleDarkTheme';

import { techRadarPlugin } from '@backstage-community/plugin-tech-radar';
import { HomePage } from './components/home/HomePage';
// import { LandscapePage } from '@internal/plugin-landscape';
import { OpenshiftPage } from '@internal/plugin-openshift';
import { CustomTechRadarPage } from './components/techRadar/CustomTechRadarPage';
import { NewsPage } from '@internal/backstage-plugin-news';
import { createTranslationResource } from '@backstage/core-plugin-api/alpha';
import {
  newsTranslationRef,
  newsMessagesGerman,
  newsMessagesEnglish,
} from '@internal/backstage-plugin-news';

const app = createApp({
  apis,
  plugins: [techRadarPlugin],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        providers={[
          {
            id: 'puzzle-auth-provider',
            title: 'Puzzle ITC',
            message: 'Sign in using Puzzle SSO',
            apiRef: puzzleOIDCAuthApiRef,
          },
        ]}
      />
    ),
  },
  themes: [
    {
      id: 'puzzle',
      title: 'Light Theme',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={puzzleTheme} children={children} />
      ),
    },
    {
      id: 'puzzleDark',
      title: 'Dark Theme',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={puzzleDarkTheme} children={children} />
      ),
    },
  ],
  __experimentalTranslations: {
    defaultLanguage: 'de',
    availableLanguages: ['de', 'en'],
    resources: [
      createTranslationResource({
        ref: newsTranslationRef,
        translations: {
          de: () => Promise.resolve({ default: newsMessagesGerman as any }),
          en: () => Promise.resolve({ default: newsMessagesEnglish as any }),
        },
      }),
    ],
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />}>
      <CustomCatalogPage />
    </Route>
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />}>
      <CustomTechDocsPage />
    </Route>
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<CustomTechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          groups={[
            {
              title: 'Recommended',
              filter: entity =>
                entity?.metadata?.tags?.includes('recommended') ?? false,
            },
            {
              title: 'Crossplane Composite Resources',
              filter: entity =>
                entity?.metadata?.tags?.includes('crossplane') ?? false,
            },
          ]}
        />
      }
    />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/tech-radar" element={<CustomTechRadarPage />} />
    {/* <Route path="/landscape" element={<LandscapePage />} /> */}
    <Route path="/openshift" element={<OpenshiftPage />} />
    <Route path="/news" element={<NewsPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <VisitListener />
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
